<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType } from 'dashboard/helper/inbox';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LiveChatCampaignDetails from './LiveChatCampaignDetails.vue';
import SMSCampaignDetails from './SMSCampaignDetails.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: Number,
    default: 0,
  },
  sendingStartTime: {
    type: String,
    default: '',
  },
  sendingEndTime: {
    type: String,
    default: '',
  },
  pauseReason: {
    type: String,
    default: null,
  },
  sentCount: {
    type: Number,
    default: 0,
  },
  failedCount: {
    type: Number,
    default: 0,
  },
  totalContacts: {
    type: Number,
    default: 0,
  },
  audienceNames: {
    type: Array,
    default: () => [],
  },
  conversationsWithReplies: {
    type: Number,
    default: 0,
  },
  negativeReplies: {
    type: Number,
    default: 0,
  },
  qualityRating: {
    type: String,
    default: '',
  },
});

const emit = defineEmits([
  'edit',
  'delete',
  'viewReport',
  'resume',
  'pause',
  'restart',
  'continueNew',
  'retryFailed',
  'viewFailures',
  'viewReplies',
]);

const { t } = useI18n();

const { formatMessage } = useMessageFormatter();

const isCompleted = computed(() => props.status === 'completed');
const isSending = computed(
  () =>
    props.status === 'sending' ||
    props.status === 'processing' ||
    props.status === 'running'
);
const isPaused = computed(() => props.status === 'paused');
const isFailed = computed(() => props.status === 'failed');

const isActive = computed(() =>
  props.isLiveChatType ? props.isEnabled : !isCompleted.value
);

const canResume = computed(
  () => !props.isLiveChatType && !isSending.value && !isCompleted.value
);
const canPause = computed(() => !props.isLiveChatType && isSending.value);
const canRestart = computed(() => !props.isLiveChatType);

const statusTextColor = computed(() => {
  if (props.isLiveChatType) {
    return {
      'text-n-teal-11': isActive.value,
      'text-n-slate-12': !isActive.value,
    };
  }
  if (isSending.value) return { 'text-n-blue-11': true };
  if (isCompleted.value) return { 'text-n-slate-12': true };
  if (isFailed.value) return { 'text-n-red-11': true };
  if (isPaused.value) return { 'text-n-amber-11': true };
  return { 'text-n-teal-11': true };
});

const statusBgColor = computed(() => {
  if (isSending.value) return 'bg-n-blue-3';
  if (isCompleted.value) return 'bg-n-alpha-2';
  if (isFailed.value) return 'bg-n-red-3';
  if (isPaused.value) return 'bg-n-amber-3';
  return 'bg-n-alpha-2';
});

const campaignStatus = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  if (isFailed.value) return 'Falhou';
  if (isPaused.value) return 'Pausada';
  if (isSending.value) return 'Enviando';
  if (isCompleted.value) return 'Concluído';
  return 'Agendada';
});

const inboxName = computed(() => props.inbox?.name || '');

const inboxIcon = computed(() => {
  const {
    medium,
    channel_type: type,
    voice_enabled: voiceEnabled,
  } = props.inbox;
  return getInboxIconByType(type, medium, 'fill', voiceEnabled);
});

const sendingWindow = computed(() => {
  if (!props.sendingStartTime && !props.sendingEndTime) return '';
  return `${props.sendingStartTime || '08:00'} - ${props.sendingEndTime || '18:00'}`;
});

const audienceLabel = computed(() => {
  if (!props.audienceNames || props.audienceNames.length === 0) return '';
  return props.audienceNames.join(', ');
});

const progressPercent = computed(() => {
  if (!props.totalContacts || props.totalContacts === 0) return 0;
  return Math.min(
    100,
    Math.round(((props.sentCount + props.failedCount) / props.totalContacts) * 100)
  );
});

const qualityLabel = computed(() => {
  const map = {
    GREEN: 'Alta',
    YELLOW: 'Média',
    RED: 'Baixa',
    UNKNOWN: 'Desconhecida',
  };
  return map[props.qualityRating] || props.qualityRating || '';
});

const qualityColor = computed(() => {
  const map = {
    GREEN: 'text-n-teal-11 bg-n-teal-3',
    YELLOW: 'text-n-amber-11 bg-n-amber-3',
    RED: 'text-n-ruby-11 bg-n-ruby-3',
  };
  return map[props.qualityRating] || 'text-n-slate-11 bg-n-alpha-2';
});

const qualityDot = computed(() => {
  const map = {
    GREEN: 'bg-n-teal-9',
    YELLOW: 'bg-n-amber-9',
    RED: 'bg-n-ruby-9',
  };
  return map[props.qualityRating] || 'bg-n-slate-9';
});
</script>

<template>
  <CardLayout layout="row">
    <div class="flex flex-col items-start justify-between flex-1 min-w-0 gap-2">
      <!-- Title + Status -->
      <div class="flex justify-between gap-3 w-fit">
        <span
          class="text-base font-medium capitalize text-n-slate-12 line-clamp-1"
        >
          {{ title }}
        </span>
        <span
          class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md"
          :class="[statusBgColor, statusTextColor]"
        >
          {{ campaignStatus }}
        </span>
      </div>

      <!-- Message preview with tooltip -->
      <div class="relative group w-full">
        <div
          v-dompurify-html="formatMessage(message, false, false, false)"
          class="text-sm text-n-slate-11 line-clamp-1 [&>p]:mb-0 h-6 cursor-default"
        />
        <div
          v-if="message"
          class="absolute left-0 top-full mt-1 z-50 hidden group-hover:block max-w-lg w-max p-3 rounded-lg shadow-lg border border-n-slate-6 bg-n-solid-2 text-sm text-n-slate-12"
        >
          <div
            v-dompurify-html="formatMessage(message, false, false, false)"
            class="[&>p]:mb-1 max-h-64 overflow-y-auto whitespace-pre-wrap"
          />
        </div>
      </div>

      <!-- Pause/failure reason -->
      <div
        v-if="pauseReason"
        class="flex items-start gap-2 w-full p-2 rounded-lg border"
        :class="
          isFailed
            ? 'bg-n-red-3 border-n-red-6'
            : 'bg-n-amber-3 border-n-amber-6'
        "
      >
        <span
          class="shrink-0 mt-0.5"
          :class="
            isFailed
              ? 'i-lucide-circle-x text-n-red-11'
              : 'i-lucide-alert-triangle text-n-amber-11'
          "
        />
        <p
          class="text-xs leading-relaxed line-clamp-2"
          :class="isFailed ? 'text-n-red-11' : 'text-n-amber-11'"
        >
          {{ pauseReason }}
        </p>
      </div>

      <!-- Campaign details row -->
      <div class="flex items-center w-full h-6 gap-2 overflow-hidden">
        <LiveChatCampaignDetails
          v-if="isLiveChatType"
          :sender="sender"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
        />
        <template v-else>
          <SMSCampaignDetails
            :inbox-name="inboxName"
            :inbox-icon="inboxIcon"
            :scheduled-at="scheduledAt"
          />
          <span
            v-if="sendingWindow"
            class="flex items-center gap-1 text-xs text-n-slate-10 flex-shrink-0"
          >
            <span class="i-lucide-clock text-xs" />
            {{ sendingWindow }}
          </span>
        </template>
      </div>

      <!-- Stats row (WhatsApp/SMS only) -->
      <div
        v-if="!isLiveChatType"
        class="flex flex-wrap items-center w-full gap-3 mt-1"
      >
        <!-- Contact list -->
        <span
          v-if="audienceLabel"
          class="flex items-center gap-1 text-xs text-n-slate-11"
          :title="audienceLabel"
        >
          <span class="i-lucide-list text-xs flex-shrink-0" />
          <span class="truncate max-w-[140px]">{{ audienceLabel }}</span>
        </span>

        <!-- Total contacts -->
        <span
          v-if="totalContacts > 0"
          class="flex items-center gap-1 text-xs text-n-slate-11"
          title="Total de contatos na lista"
        >
          <span class="i-lucide-users text-xs flex-shrink-0" />
          {{ totalContacts }} contatos
        </span>

        <!-- Sent count -->
        <span
          class="flex items-center gap-1 text-xs text-n-teal-11"
          title="Enviados com sucesso"
        >
          <span class="i-lucide-check-circle text-xs flex-shrink-0" />
          {{ sentCount }} enviados
        </span>

        <!-- Failed count -->
        <button
          v-if="failedCount > 0"
          class="flex items-center gap-1 text-xs text-n-ruby-11 hover:text-n-ruby-12 hover:underline cursor-pointer"
          title="Clique para ver detalhes das falhas"
          @click="emit('viewFailures')"
        >
          <span class="i-lucide-x-circle text-xs flex-shrink-0" />
          {{ failedCount }} falhas
        </button>

        <!-- Conversations with replies -->
        <span
          class="flex items-center gap-1 text-xs text-n-blue-11 cursor-pointer hover:underline"
          title="Ver respostas"
          @click.stop="emit('viewReplies', 'all')"
        >
          <span class="i-lucide-message-circle text-xs flex-shrink-0" />
          {{ conversationsWithReplies }} respostas
          <span v-if="sentCount > 0" class="text-n-slate-9">
            ({{ Math.round((conversationsWithReplies / sentCount) * 100) }}%)
          </span>
        </span>

        <!-- Negative replies -->
        <span
          v-if="negativeReplies > 0"
          class="flex items-center gap-1 text-xs text-n-ruby-11 cursor-pointer hover:underline"
          title="Ver respostas negativas"
          @click.stop="emit('viewReplies', 'negative')"
        >
          <span class="i-lucide-thumbs-down text-xs flex-shrink-0" />
          {{ negativeReplies }} negativas
        </span>

        <!-- Progress bar -->
        <div
          v-if="totalContacts > 0"
          class="flex items-center gap-1.5 flex-shrink-0"
          :title="`Progresso: ${progressPercent}%`"
        >
          <div class="w-16 h-1.5 bg-n-alpha-3 rounded-full overflow-hidden">
            <div
              class="h-full rounded-full transition-all"
              :class="progressPercent === 100 ? 'bg-n-teal-9' : 'bg-n-blue-9'"
              :style="{ width: `${progressPercent}%` }"
            />
          </div>
          <span class="text-xs text-n-slate-10">{{ progressPercent }}%</span>
        </div>

        <!-- API Quality -->
        <span
          v-if="!isLiveChatType"
          class="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded"
          :class="qualityRating ? qualityColor : 'text-n-slate-11 bg-n-alpha-2'"
          title="Qualidade da API WhatsApp"
        >
          <span
            class="w-1.5 h-1.5 rounded-full flex-shrink-0"
            :class="qualityRating ? qualityDot : 'bg-n-slate-9'"
          />
          Qualidade: {{ qualityLabel || '--' }}
        </span>
      </div>
    </div>
    <div class="flex items-center justify-end gap-2">
      <Button
        v-if="showAnalytics"
        v-tooltip.top="t('CAMPAIGN.WHATSAPP.CARD.ANALYTICS')"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-chart-no-axes-column"
        :title="t('CAMPAIGN.WHATSAPP.CARD.ANALYTICS')"
        @click="emit('analytics')"
      />
      <Button
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-pencil"
        title="Editar campanha"
        @click="emit('edit')"
      />
      <Button
        v-if="canPause"
        variant="faded"
        size="sm"
        color="amber"
        icon="i-lucide-pause"
        title="Pausar envios"
        @click="emit('pause')"
      />
      <Button
        v-if="canResume"
        variant="faded"
        size="sm"
        color="blue"
        icon="i-lucide-play"
        title="Continuar envios (de onde parou)"
        @click="emit('resume')"
      />
      <Button
        v-if="canRestart && isCompleted"
        variant="faded"
        size="sm"
        color="blue"
        icon="i-lucide-user-plus"
        title="Continuar apenas para novos contatos (preserva envios anteriores)"
        @click="emit('continueNew')"
      />
      <Button
        v-if="failedCount > 0"
        variant="faded"
        size="sm"
        color="amber"
        icon="i-lucide-refresh-cw"
        title="Reenviar apenas os contatos que falharam"
        @click="emit('retryFailed')"
      />
      <Button
        v-if="canRestart"
        variant="faded"
        size="sm"
        color="teal"
        icon="i-lucide-rotate-ccw"
        title="Reiniciar campanha do zero (limpa todos os envios)"
        @click="emit('restart')"
      />
      <Button
        v-if="!isLiveChatType"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-chart-bar"
        title="Ver histórico de envios"
        @click="emit('viewReport')"
      />
      <Button
        variant="faded"
        color="ruby"
        size="sm"
        icon="i-lucide-trash"
        @click="emit('delete')"
      />
    </div>
  </CardLayout>
</template>
